import { Button, Popover, Upload } from 'antd';
import { RcFile } from 'antd/lib/upload/interface';
import cx from 'classnames';
import { useCallback, useState } from 'react';
import DocumentItem from 'commons/components/items/DocumentItem';
import Section from 'commons/components/section/Section';
import { REQUEST_STATUSES } from 'commons/types/common';
import {
  DOCUMENT_TYPES,
  DocumentType,
  DocumentTypeText,
} from 'commons/types/documentsTypes';
import { useDocumentList } from 'stores/documents/documentsHooks';
import {
  createDocumentAsync,
  getCreateDocumentRequestStatus,
  getDocumentAsync,
} from 'stores/documents/documentsSlice';
import { useAppDispatch, useAppSelector } from 'stores/hooks';

export default function Documents() {
  const dispatch = useAppDispatch();
  const [documents, fetchDocumentsRequestStatus] = useDocumentList();
  const createDocumentRequestStatus = useAppSelector(
    getCreateDocumentRequestStatus,
  );
  const [openPopOver, setOpenPopOver] = useState(false);
  const className = cx('flex flex-col grow items-center ', {
    'justify-center': !documents || documents.length === 0,
    'justify-between': documents && documents.length > 0,
  });
  // To prevent antd from trying to upload the file
  const customRequest = useCallback(() => {}, []);
  const onClick = useCallback(
    (visible: boolean) => setOpenPopOver(visible),
    [],
  );
  const onUpload = useCallback(
    (type: DocumentType) => (file: RcFile) => {
      dispatch(createDocumentAsync({ document: file, type }));
      setOpenPopOver(false);
      return ''; // Just a wtf return type from antd ;)
    },
    [dispatch],
  );
  const onOpenDocument = useCallback(
    (documentID: string) => () => dispatch(getDocumentAsync(documentID)),
    [dispatch],
  );

  return (
    <div className={className}>
      {documents && documents.length > 0 && (
        <div className='flex flex-col w-full self-start grow space-y-3'>
          <Section
            title='Mes documents envoyés'
            loading={fetchDocumentsRequestStatus === REQUEST_STATUSES.PENDING}
            childrens={documents
              .filter((document) => !document.received)
              .map((document) => (
                <DocumentItem
                  key={document.id}
                  date={document.created}
                  type={document.type}
                  onClick={onOpenDocument(document.id)}
                  clickable
                />
              ))}
          />
          <Section
            title='Mes documents reçus'
            loading={fetchDocumentsRequestStatus === REQUEST_STATUSES.PENDING}
            childrens={documents
              .filter((document) => document.received)
              .map((document) => (
                <DocumentItem
                  key={document.id}
                  date={document.created}
                  type={document.type}
                  onClick={onOpenDocument(document.id)}
                  clickable
                />
              ))}
          />
        </div>
      )}
      <div className='flex flex-col justify-center items-center'>
        {(!documents || documents.length === 0) && (
          <p className='text-border-light px-16'>
            Aucun document téléchargé pour le moment
          </p>
        )}

        <Popover
          placement='top'
          open={openPopOver}
          onOpenChange={onClick}
          trigger={['click']}
          getPopupContainer={(trigger) =>
            (trigger.parentNode as HTMLElement) || document.body
          }
          content={
            <div className='flex flex-col'>
              {[DOCUMENT_TYPES.ORDONNANCE, DOCUMENT_TYPES.JUSTIFICATIF_CMU].map(
                (type: DocumentType) => {
                  const content = DocumentTypeText[type];
                  return (
                    <Upload
                      key={type}
                      name='file'
                      multiple={false}
                      maxCount={1}
                      action={onUpload(type)}
                      showUploadList={false}
                      customRequest={customRequest}
                      className='leading-2 w-56 hover:font-semibold'
                    >
                      <span className='text-lg'>{content}</span>
                    </Upload>
                  );
                },
              )}
            </div>
          }
        >
          <Button
            type='primary'
            size='large'
            loading={createDocumentRequestStatus === REQUEST_STATUSES.PENDING}
            className='!my-8 !rounded-[32px] !w-80 !h-14 !font-semibold'
          >
            Ajouter un document
          </Button>
        </Popover>
      </div>
    </div>
  );
}
